import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { UserState } from './UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { initCap } from '../utils/format';

export type INotificationKey = keyof IUserSettings['notifications'];

export type INotificationType = 'email' | 'push';

export interface IUserSettings {
    notifications: {
        scheduledTips: {
            enabled: boolean;
        },
        dailyLog: {
            enabled: boolean;
        },
        dailyTasks?: {
            enabled: boolean;
        },
        community?: {
            pushDisabled?: boolean;
            emailDisabled?: boolean;
        },
        care?: {
            pushDisabled?: boolean;
        }
    }
};

export class UserSettingsState extends State<IUserSettings | undefined> {
    static inject = () => [
        APIClient,
        UserState,
        MixPanelEventEmitter
    ];

    constructor(
        private apiClient: APIClient,
        private userState: UserState,
        private mixpanel: MixPanelEventEmitter
    ) {
        super(undefined);
    };

    load = async () => {
        const settings = await this.apiClient.get(this.userState.state.links?.settings);
        if (settings) {
            this.setState(settings);
        }
    };

    save = async () => {
        const settings = await this.apiClient.put(this.userState.state.links?.settings, this.state);
        this.setState(settings);
    };

    private track = (key: INotificationKey, type: INotificationType, value: boolean) => {
        const trackingLabels: {[key in INotificationKey]: string} = {
            'scheduledTips': 'Tips & Inspiration',
            'dailyLog': 'Daily Care Journal',
            'dailyTasks': 'Daily Task Reminders',
            'community': 'Community Updates',
            'care': 'Care Updates'
        };

        const label = `${trackingLabels[key]} ${initCap(type)} Notifications Toggled`;

        this.mixpanel.track(label, () => ({
            'Toggled To': value ? 'On' : 'Off'
        }));
    };

    updateNotificationSetting = (key: INotificationKey, type: INotificationType, value: boolean) => {
        let notification;

        // handle legacy settings that were stored differently
        if (key === 'dailyLog' || key === 'scheduledTips' || key === 'dailyTasks') {
            notification = {
                [key]: {enabled: value}
            };
        }

        else {
            notification = {
                [key]: {
                    ...this.state?.notifications.community,
                    ...{[`${type}Disabled`]: !value}
                }
            };
        }

        this.setState({...this.state, ...{notifications: {...this.state!.notifications, ...notification}}});

        this.track(key, type, value);
    };

    // warning: this is invoked from NotificationsConsentDialog when state may not be loaded yet,
    // so can't use this.updateNotificationSetting()
    disableNotifications = () => {
        const allDisabled: IUserSettings = {
            notifications: {
                scheduledTips: { enabled: false },
                dailyLog: { enabled: false },
                community: {
                    pushDisabled: true,
                    emailDisabled: true
                },
                care: {
                    pushDisabled: true
                }
            }
        };

        this.setState(allDisabled);
    };
};
